import { DatePicker, TimePicker, DateTimePicker } from '@mui/x-date-pickers-pro';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography } from '@/shared/theme/tokens';

// Project-standard MUI X (Pro) date/time pickers, themed to match our inputs.
// Replaces every native <input type="date|datetime-local|time"> in the app so
// they share one calendar/clock UI and one visual style.
//
// Values are dayjs objects. Use the string helpers from '@/shared/lib/dateHelpers'
// at call sites that store ISO strings / "HH:mm" — keeps the BE contract unchanged.

// Popper z-index must clear the FilterDrawer panel (1301) and overlay (1300),
// otherwise the calendar renders behind the drawer it was opened from.
const POPPER_Z = 1402;

function usePickerSlotProps(extraTextField) {
  const c = useThemeColors();
  const isDark = useIsDark();

  // Themed calendar/clock surface — MUI's Paper would otherwise use the theme
  // default and (before the mode-aware palette) render light in dark mode. We
  // style it explicitly so the popup always matches our tokens.
  const paperSx = {
    background: c.bg,
    color: c.textPrimary,
    border: `1px solid ${c.border}`,
    borderRadius: '12px',
    boxShadow: isDark ? '0 8px 28px rgba(0,0,0,0.55)' : '0 8px 28px rgba(30,67,150,0.16)',
    fontFamily: typography.fontFamily.body,
    '& .MuiPickersCalendarHeader-label, & .MuiDayCalendar-weekDayLabel, & .MuiPickersDay-root, & .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton, & .MuiMultiSectionDigitalClockSection-item': {
      color: c.textPrimary,
      fontFamily: typography.fontFamily.body,
    },
    '& .MuiDayCalendar-weekDayLabel': { color: c.textMuted },
    '& .MuiPickersDay-root:hover': { background: c.bgHover },
    '& .MuiPickersDay-root.Mui-selected, & .MuiMultiSectionDigitalClockSection-item.Mui-selected': {
      background: `${c.textActive} !important`, color: '#fff',
    },
    '& .MuiPickersArrowSwitcher-button, & .MuiPickersCalendarHeader-switchViewButton': { color: c.textMuted },
  };

  return {
    textField: {
      size: 'small',
      fullWidth: true,
      ...extraTextField,
      sx: {
        '& .MuiInputBase-root': {
          fontSize: 13,
          fontFamily: typography.fontFamily.body,
          color: c.textPrimary,
          background: c.bg,
          borderRadius: '8px',
        },
        '& .MuiInputBase-input': { padding: '8px 10px' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: c.borderSubtle ?? c.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.textActive },
        '& .MuiSvgIcon-root': { color: c.textMuted, fontSize: 18 },
        ...(extraTextField?.sx ?? {}),
      },
    },
    popper: { sx: { zIndex: POPPER_Z } },
    desktopPaper: { sx: paperSx },
    mobilePaper: { sx: paperSx },
  };
}

export function AppDatePicker({ slotProps, ...props }) {
  const themed = usePickerSlotProps(slotProps?.textField);
  return <DatePicker slotProps={{ ...slotProps, ...themed }} {...props} />;
}

export function AppTimePicker({ slotProps, ...props }) {
  const themed = usePickerSlotProps(slotProps?.textField);
  return <TimePicker slotProps={{ ...slotProps, ...themed }} {...props} />;
}

export function AppDateTimePicker({ slotProps, ...props }) {
  const themed = usePickerSlotProps(slotProps?.textField);
  return <DateTimePicker slotProps={{ ...slotProps, ...themed }} {...props} />;
}
